import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { CustomRoute } from '../interfaces/custom-route.interface';
import { CustomRouteResponse, DailyRouteResponse, RegisterUserBeforeFormValuesResponse } from '../interfaces/custom-route.response';
import { HttpClient } from '@angular/common/http';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PlannerDayStateService } from '../shared/planner/services';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CustomRouteService {
  // Service injections
  private _http = inject(HttpClient);
  private _sessionStorageService = inject(SessionStorageService);
  private _plannerDayStateService = inject(PlannerDayStateService);

  // State service signals
  private _accumulatedDailyRoutes = signal<DailyRouteResponse[]>([]);
  public accumulatedDailyRoutes = this._accumulatedDailyRoutes.asReadonly();

  private _originalCustomRouteData = signal<CustomRoute | null>(null);
  public originalCustomRouteData = this._originalCustomRouteData.asReadonly();

  private _token = signal<string | null>(null);
  public token = this._token.asReadonly();

  private _routeUpdated = signal<boolean>(false);
  public routeUpdated = this._routeUpdated.asReadonly();

  private _registerUserBeforeFormValues = signal<RegisterUserBeforeFormValuesResponse | null>(null);
  public registerUserBeforeFormValues = this._registerUserBeforeFormValues.asReadonly();

  private _isWelcomeViewShown = signal<boolean>(false);
  public isWelcomeViewShown = this._isWelcomeViewShown.asReadonly();

  private _isLoadingCustomRouteData = signal<boolean>(false);
  public isLoadingCustomRouteData = this._isLoadingCustomRouteData.asReadonly();

  /**
   * Setters
   */
  setOriginalCustomRouteData(customRoute: CustomRoute): void {
    this._originalCustomRouteData.set(customRoute);
    this._sessionStorageService.setItem('originalCustomRouteData', customRoute);
  }

  /** Null desmonta el planner del embed (routeReady → false) — `reset()` y el
   *  fallo de una ruta compartida (HRS-2890) ya lo pasan. */
  setToken(token: string | null): void {
    this._token.set(token);
  }

  setRegisterUserBeforeFormValues(values: RegisterUserBeforeFormValuesResponse | null): void {
    this._registerUserBeforeFormValues.set(values);
  }

  setIsWelcomeViewShown(isShown: boolean): void {
    this._isWelcomeViewShown.set(isShown);
    this._sessionStorageService.setItem('customRouteWelcomeView', isShown);
  }

  // Http requests
  public notifyPlannerExit(userId: string, serviceId: number, clientType: string, routeToken: string): Observable<{ status: string; message_id: string }> {
    const url = `${environment.apiAssistantUrlBase}/api/planner/exit`;
    const body = {
      user_id: userId,
      service_id: serviceId,
      client_type: clientType,
      route_token: routeToken
    };

    return this._http.post<{ status: string; message_id: string }>(url, body);
  }

  public getCustomRouteResponse(
    token: string, 
    client_id: number, 
    client_type: string, 
    app_id: number,
    is_recommended = false
  ): Observable<CustomRouteResponse> 
  {
    const url = `${environment.apiAssistantUrlBase}/api/get-route-completed`;
    const body = {
      token: token,
      client_id: client_id,
      client_type: client_type,
      app_id: app_id,
      is_recommended: is_recommended
    }

    this._isLoadingCustomRouteData.set(true);

    return this._http.post<CustomRouteResponse>(
      url,
      body,
      { observe: 'response' }  // include status, headers, etc.
    ).pipe(
      map((httpResponse) => {
        this._isLoadingCustomRouteData.set(false);
        const status = httpResponse.status;
        const responseBody = httpResponse.body;

        console.log("HTTP Status:", status);
        console.log("Response Body:", responseBody);

        // Combina status y body en un solo objeto
        //return EDITED_CUSTOM_ROUTE_DATA_MOCK as CustomRouteResponse;
        return {
          status,
          ...(responseBody as object)
        } as CustomRouteResponse;
      }),
      catchError(error => {
        this._isLoadingCustomRouteData.set(false);
        console.error(`Error fetching custom route data: ${error}`);
        return throwError(() => new Error('HTTP request failed', { cause: error })); // Propagate the error
      })
    );
  }

  /**
   * Resets
   */
  resetRouteUpdate() {
    this._routeUpdated.set(false);
  }

  getCoordinates(): { lat: number; lng: number } | null {
    const coordinates = this.registerUserBeforeFormValues()?.coordinates;
    if (!coordinates) 
      return null;

    const parts = coordinates.split(",");
    if (parts.length !== 2) 
      return null;

    const lat = Number(parts[0].trim());
    const lng = Number(parts[1].trim());
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) 
      return null;

    return { lat, lng };
  }

  /**
   * Resets the service state, clearing accumulated daily routes,
   * custom route data, token, and current order.
   * It is called every time you go to register-user.component.ts
   * or when generating a NEW route (evita reutilizar token/ruta anterior).
   */
  reset() {
    this._accumulatedDailyRoutes.set([]);
    this.setOriginalCustomRouteData(null);
    this.setToken(null);
    this._registerUserBeforeFormValues.set(null);
    this._routeUpdated.set(false);
    this._plannerDayStateService.resetDayToShowIndex();
    this._sessionStorageService.removeItem('originalCustomRouteData');
    this._sessionStorageService.removeItem('numberOfDaysCustomRoute');
    this._sessionStorageService.removeItem('customRouteOrder');
    this._sessionStorageService.removeItem('showDevPanel');
    this._isWelcomeViewShown.set(false);
    this._sessionStorageService.removeItem('customRouteWelcomeView');
  }
}
